import { supabase } from '@/core/supabase/client';

export type MobilityMode = 'none' | 'per_km_fuel' | 'per_km_fixed' | 'flat' | 'manual';

export interface Client {
  id: string;
  organization_id: string;
  name: string;
  record_kind: 'commercial' | 'legacy_placeholder';
  tax_id: string | null;
  notes: string | null;
  is_active: boolean;
  legacy_name: string | null;
  /** Facturador y reparto habituales de este cliente. `null` = usa los de la organización. */
  default_biller_participant_id: string | null;
  default_split_template_id: string | null;
  /**
   * El campo por el que apareció en la búsqueda, si apareció por un campo.
   *
   * Sin esto, buscar «El Trebol» devolvía «Rombola Hns.» y parecía un error del
   * buscador. `null` cuando coincidió el nombre del cliente: ahí no hay nada
   * que explicar.
   */
  matched_establishment?: string | null;
}

export interface Establishment {
  id: string;
  organization_id: string;
  client_id: string;
  name: string;
  record_kind: 'real' | 'legacy_placeholder';
  /**
   * Kilómetros habituales del establecimiento.
   * PENDIENTE DE VALIDACIÓN: el número final de los nombres legacy
   * (p. ej. "... F 75") NO se interpreta como km hasta confirmarlo.
   * Este campo solo se llena con lo que el usuario carga explícitamente.
   */
  default_km: number | null;
  /** `null` = heredar de la organización (Spec §2.3), no "false". */
  charge_mobility: boolean | null;
  mobility_mode: MobilityMode | null;
  mobility_rate_cents: number | null;
  flat_mobility_cents: number | null;
  default_mobility_participant_id: string | null;
  notes: string | null;
  is_active: boolean;
}

export interface OrganizationSettings {
  organization_id: string;
  default_mobility_mode: MobilityMode;
  default_mobility_rate_cents: number | null;
  default_flat_mobility_cents: number | null;
  charge_mobility_by_default: boolean;
  /** Reparto habitual. `null` = la organización no definió ninguno. */
  default_split_template_id: string | null;
  default_biller_participant_id: string | null;
}

/** Solo clientes comerciales activos: los placeholders legacy quedan fuera (D5). */
/**
 * Busca por el nombre del cliente O por el de cualquiera de sus campos.
 *
 * Va por función y no por consulta directa por dos motivos que se descubrieron
 * usándola. El primero: en el campo uno se acuerda del campo, no del apellido
 * del dueño —«El Trebol» se dice todos los días, «Rombola Hns.» aparece en la
 * factura—. El segundo no avisa y es peor: la consulta anterior era `ilike` a
 * secas, y escribir «pacifica» devolvía CERO resultados para «LA PACÍFICA»,
 * como si el cliente no existiera.
 */
export async function searchClients(organizationId: string, term: string): Promise<Client[]> {
  const { data, error } = await supabase.rpc('search_clients', {
    p_org: organizationId,
    p_term: term,
  });
  if (error) throw error;
  return (data ?? []) as Client[];
}

/** Listado completo, incluidos inactivos y placeholders, para administración. */
export async function listAllClients(organizationId: string): Promise<Client[]> {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('organization_id', organizationId)
    .order('is_active', { ascending: false })
    .order('name');
  if (error) throw error;
  return (data ?? []) as Client[];
}

export async function createClient(input: {
  organizationId: string;
  name: string;
  taxId?: string | null;
}): Promise<Client> {
  const { data, error } = await supabase
    .from('clients')
    .insert({
      organization_id: input.organizationId,
      name: input.name.trim(),
      tax_id: input.taxId?.trim() || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as Client;
}

export async function updateClient(id: string, patch: Partial<Client>): Promise<void> {
  const { error } = await supabase.from('clients').update(patch).eq('id', id);
  if (error) throw error;
}

/**
 * Baja LÓGICA: nunca se borra un cliente con histórico (Spec §8).
 * Deja de aparecer en el buscador pero sus trabajos siguen intactos.
 */
export async function setClientActive(id: string, isActive: boolean): Promise<void> {
  await updateClient(id, { is_active: isActive });
}

export async function listEstablishments(clientId: string): Promise<Establishment[]> {
  const { data, error } = await supabase
    .from('establishments')
    .select('*')
    .eq('client_id', clientId)
    .order('is_active', { ascending: false })
    .order('name');
  if (error) throw error;
  return (data ?? []) as Establishment[];
}

/**
 * Todos los campos de la organización, agrupados por cliente.
 *
 * El listado de clientes muestra los campos de cada uno como texto secundario:
 * son lo que distingue a dos clientes de nombre parecido, y pedirlos de a uno
 * serían setenta consultas para dibujar una lista. RLS filtra igual: la
 * consulta no nombra la organización porque no puede ver otra.
 */
export async function listEstablishmentsByClient(
  organizationId: string,
): Promise<Record<string, Establishment[]>> {
  const { data, error } = await supabase
    .from('establishments')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;

  const porCliente: Record<string, Establishment[]> = {};
  for (const e of (data ?? []) as Establishment[]) {
    (porCliente[e.client_id] ??= []).push(e);
  }
  return porCliente;
}

export async function createEstablishment(input: {
  organizationId: string;
  clientId: string;
  name: string;
}): Promise<Establishment> {
  const { data, error } = await supabase
    .from('establishments')
    .insert({
      organization_id: input.organizationId,
      client_id: input.clientId,
      name: input.name.trim(),
    })
    .select()
    .single();
  if (error) throw error;
  return data as Establishment;
}

export async function updateEstablishment(
  id: string,
  patch: Partial<Establishment>,
): Promise<void> {
  const { error } = await supabase.from('establishments').update(patch).eq('id', id);
  if (error) throw error;
}

export async function fetchOrganizationSettings(
  organizationId: string,
): Promise<OrganizationSettings | null> {
  const { data, error } = await supabase
    .from('organization_settings')
    .select('*')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as OrganizationSettings | null;
}

export const MOBILITY_LABELS: Record<MobilityMode, string> = {
  none: 'No cobrar movilidad',
  per_km_fuel: 'Km × combustible',
  per_km_fixed: 'Km × tarifa fija',
  flat: 'Monto fijo',
  manual: 'Monto manual',
};

/**
 * Borrar un cliente o un campo.
 *
 * Se niegan en un solo caso, y no es por conservar historia: un trabajo sin
 * cliente no es un registro incompleto, es uno que no significa nada. La base
 * contesta cuántos trabajos hay para que la salida sea obvia —borrarlos, o
 * desactivar—, en vez de un «no se puede» sin explicación.
 */
export async function deleteClient(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_client', { p_client: id });
  if (error) throw error;
}

export async function deleteEstablishment(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_establishment', { p_est: id });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Destinatarios de facturación
// ---------------------------------------------------------------------------

/**
 * A nombre de quién se le emite la factura a este cliente.
 *
 * Es una dimensión propia, y la auditoría de agosto mostró por qué ninguna de
 * las que ya existían alcanza. «El Vapor / La María 2» es un solo campo de un
 * solo cliente, pero la factura hay que partirla entre Luis y Mercedes; si no,
 * se le termina emitiendo el 100 % a uno de los dos sin querer.
 *
 * No son participantes de la organización: son gente del cliente. Por eso son
 * nombres y no una referencia a `participants`.
 */
export interface DestinatarioFactura {
  id?: string;
  name: string;
  percentage: number;
  sort_order?: number;
}

export async function fetchClientInvoiceRecipients(
  clientId: string,
): Promise<DestinatarioFactura[]> {
  const { data, error } = await supabase.rpc('client_invoice_recipients_of', {
    p_client: clientId,
  });
  if (error) throw error;
  return (data ?? []) as DestinatarioFactura[];
}

/** Reemplaza el reparto entero. Vacío = se le factura todo al cliente. */
export async function setClientInvoiceRecipients(
  clientId: string,
  lines: DestinatarioFactura[],
): Promise<void> {
  const { error } = await supabase.rpc('set_client_invoice_recipients', {
    p_client: clientId,
    p_lines: lines.map((l, i) => ({ name: l.name, percentage: l.percentage, sort_order: i })),
  });
  if (error) throw error;
}

export async function fetchWorkInvoiceRecipients(workId: string): Promise<DestinatarioFactura[]> {
  const { data, error } = await supabase.rpc('work_invoice_recipients_of', { p_work: workId });
  if (error) throw error;
  return (data ?? []) as DestinatarioFactura[];
}

/**
 * Congela en el trabajo a quién había que facturarle.
 *
 * Es el punto que hace que esto sirva: cambiar mañana la configuración del
 * cliente no puede reescribir a quién se le facturó hace tres meses.
 */
export async function setWorkInvoiceRecipients(
  workId: string,
  lines: DestinatarioFactura[],
): Promise<void> {
  const { error } = await supabase.rpc('set_work_invoice_recipients', {
    p_work: workId,
    p_lines: lines.map((l, i) => ({ name: l.name, percentage: l.percentage, sort_order: i })),
  });
  if (error) throw error;
}
